import React, { 
  useCallback 
} from "react";
import { 
  styled, 
  Box 
} from "@mui/material";
import { 
  borderRadius,
  colors, 
  fontFamily, 
  fontSize, 
  fontWeight, 
  margin
} from "theme/defaultStyle";
import FileUploadIcon from "theme/icons/FileUploadIcon";
import { 
  ErrorCode, 
  FileRejection, 
  FileWithPath, 
  useDropzone 
} from "react-dropzone";
import { useUploadDropZoneStates } from "utils/context/UploadDropZoneContext";
import { hasValue } from "utils/shared";
import { UploadConfiguration } from "entities/UIModel";

const Container = styled(Box)({
  fontFamily: fontFamily.primary,
  "& .drop-zone": {
    marginTop: margin.xlarge,
    height: "201px",
    border: "1px dashed #384EB74D", 
    borderRadius: borderRadius.small,
    background: "#92C4DB1A", 
    minWidth: "100%",
    display: "flex",
  },
  "& .drop-zone-text": {
    margin: "auto",
    textAlign: "center",
    height: "129px",
    width: "344px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  "& .drop-zone-text.hidden": {
    visibility: "hidden",
  },
  "& .drop-zone-text .file-upload-icon": {
    margin: "auto",
  },
  "& .drop-zone-text .drag-and-drop": {
    fontWeight: fontWeight.bold1,
    fontSize: fontSize.medium,
  },
  "& .drop-zone-text .choose-files": {
    fontWeight: fontWeight.normal1,
    fontSize: fontSize.medium,
    color: colors.blue15,
    textDecoration: "underline",
  },
  "& .drop-zone-text .supported-formats": {
    fontWeight: fontWeight.normal1,
    fontSize: fontSize.small,
  },
});

type Props = {
  // disabled?: boolean;
  handleOnUpload: (acceptedFiles:FileWithPath[], fileRejections: FileRejection[]) => void;
  // Per-feature upload config; must be passed explicitly by the caller.
  uploadConfiguration?: UploadConfiguration;
}

const UploadDropZone = ({handleOnUpload, uploadConfiguration}:Props) => {
  const [{disabled, visible}] = useUploadDropZoneStates();

  const uploadConfig = uploadConfiguration;

  const allowedFileSize = uploadConfig?.allowedFileSize ? uploadConfig?.allowedFileSize : 50;
  const multiple:boolean = hasValue(uploadConfig?.multiple) ? Boolean(uploadConfig?.multiple) : true;
  const maxFileSize = (allowedFileSize * 1024 * 1024); 
  function maxFileSizeValidator(file:FileWithPath) {
    if (file.size > maxFileSize) {
      return {
        code: ErrorCode.FileTooLarge,
        message: "File size is too large",
      }
    }
  
    return null;
  }
  
  const acceptMimeTypes = uploadConfig?.acceptMimeTypes?.length ? uploadConfig.acceptMimeTypes : null;

  const getAcceptMimeTypes = () => {
    const accept = new Map();
    if (acceptMimeTypes) {
      acceptMimeTypes.forEach((acceptMimeType) => {
        const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
        accept.set(acceptMimeType.mimeType,extensions);
      })
    }
    else {
      accept.set('text/csv',['.csv']);
    }

    return accept;
  }

  const getSupportedExtensions = () => {
    if (!acceptMimeTypes) {
      return ".csv";
    }

    return acceptMimeTypes
      .map((acceptMimeType) => acceptMimeType.extensions.map((extension) => extension.extension).join(", "))
      .join(", ");
  }

  const onDrop = useCallback(async (acceptedFiles:FileWithPath[], fileRejections: FileRejection[]) => {
    await handleOnUpload(acceptedFiles, fileRejections);
  }, [])  

  const { getRootProps, getInputProps  } = useDropzone({
    // accept: {"text/csv": ['.csv']},
    accept: Object.fromEntries(getAcceptMimeTypes()),
    validator: maxFileSizeValidator,
    onDrop,
    disabled: disabled,
    multiple: multiple,
  });   
    
  return (
    <>
      <Container>
          <div className="drop-zone" {...getRootProps()}>
            <input {...getInputProps()} />
            <div className={`drop-zone-text ${visible ? "":"hidden"} `}>
              <div className="file-upload-icon">
                <FileUploadIcon />
              </div>
              <div className="drag-and-drop">
                Drag and drop register data or&nbsp;
                <span className="choose-files">choose file</span>
              </div>
              <div className="supported-formats">
                Supported description: {getSupportedExtensions()} with a maximum size of {allowedFileSize} MB
              </div>
            </div>
          </div>        
      </Container>
    </>
  )

}

export default UploadDropZone;